/**
 * Asking the deployment whether the key it was given actually works.
 *
 * ── Why this exists when there is no key field ───────────────────────────────
 *
 * The settings screen can only ever be told one thing about the key —
 * [AccountSettings.aiAvailable], a bare boolean — and that boolean answers *"is something
 * set"*. It cannot answer *"is the thing that was set working"*, and those are different
 * questions with different people behind them: a variable that reached the process but
 * carries yesterday's revoked key looks identical to a working one from the pill's side,
 * and the first anybody would otherwise hear of it is a bookmark quietly failing to be
 * summarised an hour after it was saved.
 *
 * So there is one button that makes one minimal real call and reports what came back.
 * That is the whole of it: no *Try on a URL…*, no sample output, nothing that spends the
 * operator's key on a second thing nobody asked for.
 *
 * ── The result is a body, not a status ───────────────────────────────────────
 *
 * A provider that answers "this key is rejected" is a **successful test** — the route did
 * exactly what it was asked to and has a sentence to show — so it is a 200 carrying
 * `ok = false`. The statuses this route does use are the ones where no call was made at
 * all: 401 for a stranger, 503 for a deployment with no key, 429 for somebody pressing it
 * repeatedly. One shape for both outcomes of a test that ran, and a status only where
 * there was no test.
 *
 * ── And nothing here carries the key ─────────────────────────────────────────
 *
 * [AiTestResult.message] is the one field on this wire that quotes a third party, and a
 * provider handed a malformed key habitually echoes it — whole or as a prefix — inside the
 * error it returns. The server scrubs before composing; see `AiTestRoutes.kt`, which is
 * where that is enforced and tested. Nothing in this type may ever be widened to carry
 * more about the key than the sentence does.
 *
 * @see ApiRoutes.AI_TEST
 */
package se.soderbjorn.lunapin.clientserver

import kotlinx.serialization.Serializable

/**
 * What one press of *Test connection* found out.
 *
 * @property ok whether the model provider answered. A screen draws this as the difference
 *   between a success tone and a warning tone, and nothing else depends on it.
 * @property message one sentence, written to be shown to a person as-is — the reason it
 *   worked or the reason it did not. **Never contains the API key or any part of it**,
 *   including when it is quoting the provider's own error, which is the single likeliest
 *   place a fragment of one appears.
 */
@Serializable
data class AiTestResult(
    val ok: Boolean = false,
    val message: String = "",
)
